import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { API, useAuth } from "@/App";
import { useLocale } from "@/context/LocaleContext";
import { AuthPageHeader } from "@/components/AuthPageHeader";
import { translate } from "@/lib/uiStrings";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { joinProjectByInvite } from "@/lib/inviteJoin";
import { PROJECTS_LIST_PATH } from "@/lib/routes";

/** Human-readable message from axios/FastAPI (string detail, 422 array, eller nätverk). */
function formatAuthError(err, lang) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d
      .map((e) => (typeof e === "object" && e?.msg ? e.msg : String(e?.msg ?? JSON.stringify(e))))
      .join(" · ");
  }
  if (d && typeof d === "object" && typeof d.msg === "string") return d.msg;
  const st = err?.response?.status;
  if (!err?.response && err?.message === "Network Error") {
    return translate(lang, "auth.errNetwork");
  }
  if (st) return translate(lang, "auth.errServer", { status: String(st) });
  if (err?.message) return err.message;
  return translate(lang, "auth.errGeneric");
}

export default function LoginPage() {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const { user, login, register } = useAuth();
  const { lang, t } = useLocale();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const postAuthNavDone = useRef(false);

  useEffect(() => {
    if (user === false) postAuthNavDone.current = false;
  }, [user]);

  useEffect(() => {
    if (!user || user === false) return;
    if (postAuthNavDone.current) return;
    const inv =
      searchParams.get("invite")?.trim() || localStorage.getItem("pending_invite_code")?.trim();
    postAuthNavDone.current = true;
    (async () => {
      let hadInvite = false;
      try {
        if (inv) {
          hadInvite = true;
          localStorage.removeItem("pending_invite_code");
          const pid = await joinProjectByInvite(API, inv);
          toast.success(t("invite.joinOk"));
          nav(`/project/${pid}`, { replace: true });
          return;
        }
        toast.success(t("login.welcome"));
        nav(PROJECTS_LIST_PATH, { replace: true });
      } catch {
        postAuthNavDone.current = false;
        if (hadInvite) toast.error(t("invite.invalid"));
        nav(PROJECTS_LIST_PATH, { replace: true });
      }
    })();
  }, [user, searchParams, nav, t]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
    } catch (err) {
      console.error("[auth]", err?.response?.status, err?.response?.data ?? err?.message);
      toast.error(formatAuthError(err, lang));
    } finally { setBusy(false); }
  };

  const tabBtn = (active) =>
    `flex-1 pb-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
      active
        ? "border-[var(--text)] text-[var(--text)]"
        : "border-transparent text-[var(--text-dim)] hover:text-[var(--text)]"
    }`;

  const fieldClass =
    "mt-1 h-10 rounded-lg border-[var(--line-2)] bg-[var(--bg)] shadow-none focus-visible:ring-1 focus-visible:ring-[var(--line)] focus-visible:border-[var(--line)]";

  const hasInvite = searchParams.get("invite") || localStorage.getItem("pending_invite_code");

  return (
    <div className="min-h-screen grain flex flex-col bg-[var(--bg)] overflow-x-hidden">
      <AuthPageHeader />
      <div className="flex flex-1 flex-col items-center justify-center px-4 pb-8">
        <div className="w-full max-w-[min(400px,100%)] rounded-2xl border border-[var(--line-2)] bg-[var(--panel-2)] shadow-sm p-5 sm:p-6">
          <header className="mb-4">
            <p className="text-[var(--text-dim)] text-sm leading-snug">
              {t("login.tagline")}
            </p>
            {hasInvite && (
              <p
                className="mt-3 rounded-lg border border-[var(--accent)]/40 bg-[var(--accent)]/10 px-3 py-2 text-sm leading-snug text-[var(--accent)]"
                data-testid="invite-banner"
              >
                {t("login.inviteBanner")}
              </p>
            )}
          </header>

          <div className="flex gap-0 mb-4 border-b border-[var(--line-2)]">
            <button
              type="button"
              data-testid="tab-login"
              onClick={() => setMode("login")}
              className={tabBtn(mode === "login")}
            >
              {t("login.tabLogin")}
            </button>
            <button
              type="button"
              data-testid="tab-register"
              onClick={() => setMode("register")}
              className={tabBtn(mode === "register")}
            >
              {t("login.tabRegister")}
            </button>
          </div>

          <form onSubmit={onSubmit} className="space-y-3">
            <div className={mode === "register" ? undefined : "invisible"} aria-hidden={mode !== "register"}>
              <Label className="text-xs font-medium text-[var(--text-dim)]">{t("login.labelName")}</Label>
              <Input
                data-testid="input-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required={mode === "register"}
                tabIndex={mode === "register" ? 0 : -1}
                className={fieldClass}
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-[var(--text-dim)]">{t("login.labelEmail")}</Label>
              <Input
                data-testid="input-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className={fieldClass}
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-[var(--text-dim)]">{t("login.labelPassword")}</Label>
              <div className="relative">
                <Input
                  data-testid="input-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className={`${fieldClass} pr-11`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t("login.hidePassword") : t("login.showPassword")}
                  className="absolute right-0 top-0 inline-flex h-full w-11 items-center justify-center text-[var(--text-dim)] transition-colors hover:text-[var(--text)] focus:outline-none focus-visible:text-[var(--text)]"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div
              className={`flex items-center justify-end pt-0.5${mode === "login" ? "" : " invisible"}`}
              aria-hidden={mode !== "login"}
            >
              <Link
                to="/forgot-password"
                tabIndex={mode === "login" ? 0 : -1}
                className="text-sm text-[var(--text-dim)] transition-colors hover:text-[var(--text)]"
              >
                {t("login.forgotPassword")}
              </Link>
            </div>
            <button
              type="submit"
              data-testid="btn-submit"
              disabled={busy}
              className="w-full h-10 rounded-lg bg-[var(--text)] text-[var(--bg)] text-sm font-semibold tracking-wide hover:opacity-90 transition-opacity disabled:opacity-45 disabled:pointer-events-none"
            >
              {busy ? t("login.submitBusy") : mode === "login" ? t("login.submitLogin") : t("login.submitRegister")}
            </button>
          </form>
        </div>
        <Link
          to="/help"
          state={{ returnTo: "/login" }}
          data-testid="login-help-link"
          className="mt-5 text-sm text-[var(--text-dim)] transition-colors hover:text-[var(--text)]"
        >
          {t("help.nav")}
        </Link>
      </div>
    </div>
  );
}
